import { useEffect } from "react";

/**
 * useDynamicManifest — swap the document's PWA manifest (and iOS
 * apple-touch-icon + theme-color) while a donor/attendee is on a specific
 * event page or org microsite, so installing the app yields a white-labeled
 * home-screen icon named after that event/org.
 *
 * Android reads <link rel="manifest">; iOS Safari ignores the manifest's
 * icons and uses <link rel="apple-touch-icon"> instead, so we inject both.
 *
 * Everything is restored on unmount, so the global platform manifest stays
 * intact everywhere else.
 *
 * Params:
 *   manifestUrl     — absolute backend URL, e.g. `${API}/manifest/org/{slug}`
 *   appleTouchIcon? — absolute URL to the org/event logo (iOS home icon)
 *   themeColor?     — hex string for the iOS/Android status bar
 */
export function useDynamicManifest({ manifestUrl, appleTouchIcon, themeColor }) {
    useEffect(() => {
        if (!manifestUrl || typeof document === "undefined") return undefined;
        const head = document.head;

        // 1) Manifest link — point the existing one at our dynamic URL.
        let manifestEl = head.querySelector('link[rel="manifest"]');
        const prevManifestHref = manifestEl ? manifestEl.getAttribute("href") : null;
        const createdManifest = !manifestEl;
        if (!manifestEl) {
            manifestEl = document.createElement("link");
            manifestEl.setAttribute("rel", "manifest");
            head.appendChild(manifestEl);
        }
        manifestEl.setAttribute("href", manifestUrl);

        // 2) iOS apple-touch-icon — hide the platform ones, add a scoped one.
        const hiddenApple = [];
        let appleEl = null;
        if (appleTouchIcon) {
            head.querySelectorAll('link[rel="apple-touch-icon"]').forEach((el) => {
                el.setAttribute("rel", "apple-touch-icon-cf-disabled");
                hiddenApple.push(el);
            });
            appleEl = document.createElement("link");
            appleEl.setAttribute("rel", "apple-touch-icon");
            appleEl.setAttribute("href", appleTouchIcon);
            head.appendChild(appleEl);
        }

        // 3) theme-color — update the status-bar tint to the org/event color.
        let themeEl = null;
        let prevTheme = null;
        if (themeColor) {
            themeEl = head.querySelector('meta[name="theme-color"]');
            if (themeEl) {
                prevTheme = themeEl.getAttribute("content");
                themeEl.setAttribute("content", themeColor);
            }
        }

        return () => {
            if (createdManifest) {
                manifestEl.remove();
            } else if (prevManifestHref) {
                manifestEl.setAttribute("href", prevManifestHref);
            }
            if (appleEl) appleEl.remove();
            hiddenApple.forEach((el) => el.setAttribute("rel", "apple-touch-icon"));
            if (themeEl && prevTheme !== null) themeEl.setAttribute("content", prevTheme);
        };
    }, [manifestUrl, appleTouchIcon, themeColor]);
}
